import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useState } from 'react';
import { AlertDialog, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useModelDelete } from '@rhino-project/core/hooks';
import type { RhinoRead } from '@rhino-project/core';
import { formatActivityDate } from '../../utils/dates';

type Note = RhinoRead<'note'>;

type NoteListEntry = Pick<Note, 'id' | 'note' | 'created_at'> & {
  reporter?: Partial<Note['reporter']> | null;
  _permissions?: Note['_permissions'];
};

interface NoteListItemProps {
  note: NoteListEntry;
}

const NoteListItem = ({ note }: NoteListItemProps) => {
  const { t } = useTranslation();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: destroyNote, isPending: isDeleting } = useModelDelete('note');
  // Let the server-computed policy decide; _permissions.destroy is true only
  // for the note's author (see AdminNotePolicy).
  const canDelete = note._permissions?.destroy === true;

  const handleDelete = useCallback(() => {
    destroyNote(note.id, {
      onSuccess: () => setIsDeleteOpen(false)
    });
  }, [destroyNote, note.id]);

  return (
    <div className="rounded-lg border-t border-separator px-2 py-3 first:border-t-0 hover:bg-foreground/[0.03]">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-xs text-muted">
          {note.reporter?.display_name as string}
        </span>
        <span className="shrink-0 whitespace-nowrap text-xs text-muted tabular-nums">
          {formatActivityDate(note.created_at)}
        </span>
        {canDelete && (
          <span className="-my-1 shrink-0">
            <AlertDialog isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label={t('frontend.messages.delete_note')}
                className="text-muted hover:text-danger"
              >
                <Icon icon="bi:trash" width="14" />
              </Button>
              <AlertDialog.Backdrop>
                <AlertDialog.Container size="sm">
                  <AlertDialog.Dialog>
                    <AlertDialog.CloseTrigger />
                    <AlertDialog.Header>
                      <AlertDialog.Icon status="danger" />
                      <AlertDialog.Heading>
                        {t('frontend.messages.delete_note_2')}
                      </AlertDialog.Heading>
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                      {t(
                        'frontend.messages.are_you_sure_you_want_to_delete_this_note_this_action'
                      )}
                      <div className="mt-3 rounded-md bg-muted/30 p-2 text-sm italic text-muted wrap-break-word">
                        &ldquo;{note.note}&rdquo;
                      </div>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                      <Button
                        variant="ghost"
                        onPress={() => setIsDeleteOpen(false)}
                      >
                        {t('frontend.messages.cancel')}
                      </Button>
                      <Button
                        variant="danger"
                        onPress={handleDelete}
                        isDisabled={isDeleting}
                      >
                        {isDeleting
                          ? t('frontend.messages.deleting')
                          : t('frontend.messages.delete_note_2')}
                      </Button>
                    </AlertDialog.Footer>
                  </AlertDialog.Dialog>
                </AlertDialog.Container>
              </AlertDialog.Backdrop>
            </AlertDialog>
          </span>
        )}
      </div>
      <p className="mt-1.5 whitespace-pre-line wrap-break-word text-sm leading-relaxed">
        {note.note}
      </p>
    </div>
  );
};

export default NoteListItem;
